/**
 * The browser InputManager: the platform-neutral manager (`createInputManagerWith`, input/manager-core.ts) wired to
 * the DOM: keyboard on `window`, the Gamepad API, the DOM touch overlay (touch-overlay.ts), coarse-pointer touch
 * detection and `navigator.vibrate`.
 */
import { browserGamepadSource } from './browser-gamepad';
import { createInputManagerWith } from '@hf/client-core/input/manager';
import type { GamepadSource } from '@hf/client-core/input/gamepad';
import { hasTouchScreen, TouchOverlay } from './touch-overlay';
import type { InputManager } from '@hf/client-core/input/types';

export { CAPTURE_CANCEL_HOLD_MS, LOGICAL_BUTTONS } from '@hf/client-core/input/manager';

export interface InputManagerOptions {
  /** Keyboard event target (default window; tests pass an EventTarget; null = no keyboard). */
  target?: Window | null;
  /** Gamepad API source (default navigator.getGamepads; null = no pads). */
  gamepads?: GamepadSource | null;
  /** Clock for menu auto-repeat (default performance.now). */
  now?: () => number;
  /** Display-frame scheduler for the menu/hot-plug poll (default rAF; tests pass a manual one). */
  requestFrame?: ((cb: () => void) => number) | null;
  cancelFrame?: (id: number) => void;
  /** Mount the touch overlay (default true in browsers). */
  touchOverlay?: boolean;
  /** Overlay parent (default document.body). */
  overlayParent?: HTMLElement;
  /** Touch-screen detection (default: coarse pointer). */
  touchCapable?: () => boolean;
}

/** Legacy signature: `createInputManager(eventTarget)` = keyboard only on that target (tests). */
const normalizeOptions = (o: InputManagerOptions | Window | null): InputManagerOptions => {
  if (o === null) return { target: null, gamepads: null, requestFrame: null, touchOverlay: false };
  if (typeof (o as EventTarget).addEventListener === 'function')
    return { target: o as Window, gamepads: null, requestFrame: null, touchOverlay: false };
  return o as InputManagerOptions;
};

const vibrate = (ms: number): void => {
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    try {
      navigator.vibrate(ms);
    } catch {
      /* ignore */
    }
  }
};

export const createInputManager = (optsOrTarget: InputManagerOptions | Window | null = {}): InputManager => {
  const opts = normalizeOptions(optsOrTarget);
  const hasWindow = typeof window !== 'undefined';
  const target = opts.target === undefined ? (hasWindow ? window : null) : opts.target;
  const mountOverlay = opts.touchOverlay ?? (hasWindow && typeof document !== 'undefined');
  const parent = opts.overlayParent;
  return createInputManagerWith({
    target,
    gamepads: opts.gamepads === undefined ? browserGamepadSource() : opts.gamepads,
    ...(opts.now ? { now: opts.now } : {}),
    ...(opts.requestFrame !== undefined ? { requestFrame: opts.requestFrame } : {}),
    ...(opts.cancelFrame ? { cancelFrame: opts.cancelFrame } : {}),
    createTouchOverlay: mountOverlay
      ? (state, o) => new TouchOverlay(state, { ...(parent ? { parent } : {}), onPause: o.onPause })
      : null,
    touchCapable: opts.touchCapable ?? hasTouchScreen,
    vibrate,
  });
};
